import React, { useMemo, useState } from 'react'
import {
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Bar,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    ComposedChart,
    Legend,
} from 'recharts'
import { getAnalyticsColor, darkenColor, formatYAxisLabelWithWrap, calculateYAxisWidth, chartLabelStyle } from 'app/analytics'
import { ChartPaginationControls } from 'components'

// Duration formatting utility
const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds < 0) return '0s'
    
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    
    if (days > 0) {
        const remainingHours = hours % 24
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
    } else if (hours > 0) {
        const remainingMinutes = minutes % 60
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
    } else if (minutes > 0) {
        const remainingSeconds = seconds % 60
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
    } else {
        return `${seconds}s`
    }
}

// Duration field detection
const isDurationField = (fieldName: string): boolean => {
    if (!fieldName) return false
    const lowerName = fieldName.toLowerCase()
    return lowerName.includes('duration') || lowerName.includes('time') || lowerName.includes('elapsed')
}

// Custom tooltip formatter for duration values
const CustomTooltip = ({ active, payload, label, isDuration, hoveredSeries }: any) => {
    if (active && payload && payload.length) {
        const sortedPayload = hoveredSeries
            ? [...payload].sort((a: any, b: any) => {
                if (a?.dataKey === hoveredSeries) return -1
                if (b?.dataKey === hoveredSeries) return 1
                return 0
            })
            : payload

        return (
            <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                <p className="label">{`${label}`}</p>
                {sortedPayload.map((entry: any, index: number) => (
                    <p
                        key={index}
                        style={{
                            color: entry.color,
                            opacity: hoveredSeries !== null && entry?.dataKey !== hoveredSeries ? 0.65 : 1,
                            transition: 'opacity 0.15s ease',
                        }}
                    >
                        {`${entry.name}: ${isDuration && typeof entry.value === 'number' ? formatDuration(entry.value) : entry.value}`}
                    </p>
                ))}
            </div>
        )
    }
    return null
}

interface CustomLabelProps {
    x: number
    y: number
    width: number
    height: number
    value: string | number
    fill: string
    isDuration?: boolean
}

function CustomLabel(props: CustomLabelProps) {
    const {
        x,
        y,
        width,
        height,
        value,
        fill,
        isDuration = false
    } = props

    // Validate coordinates to prevent NaN warnings
    if (x === undefined || y === undefined || width === undefined || height === undefined
        || Number.isNaN(Number(x)) || Number.isNaN(Number(y))
        || Number.isNaN(Number(width)) || Number.isNaN(Number(height))) {
        return null
    }

    // Use a darkened version of the bar color for better readability
    const labelColor = darkenColor(fill || '#2c3e50', 25)

    // Format the value if it's a duration
    let displayValue = value
    if (isDuration && typeof value === 'number') {
        displayValue = formatDuration(value)
    }

    // Position label at the end of the horizontal bar (to the right)
    return <text
        x={Number(x) + Number(width) + 8}
        y={Number(y) + Number(height) / 2 + 4}
        style={chartLabelStyle}
        fill={labelColor}
        textAnchor="start">
        {displayValue}
    </text>
}

interface AnalyticsTopChartProps {
    title: string,
    data: { [key: string]: string | number }[],
    dataKey: string,
    labelKey: string,
    color?: string,
    textColor?: string,
    transitioning?: boolean,
    filters?: string[],
    filterKey?: string,
    compact?: boolean,
    limit?: number,
    isDuration?: boolean,
    durationScale?: { scale: number, unit: string, unitLabel: string }
    hasToggles?: boolean,
    // Pagination props
    pageSize?: number,
    showPagination?: boolean,
    // New props for multi-series support
    series?: Array<{
        dataKey: string,
        name: string,
        color?: string,
        isDuration?: boolean,
        durationScale?: { scale: number, unit: string, unitLabel: string }
    }>,
    // Margin modifier prop
    marginModifier?: number
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: any
    }) => void
}

export function AnalyticsTopChart(props: AnalyticsTopChartProps) {
    const {
        title,
        data,
        dataKey,
        labelKey,
        color,
        isDuration = false,
        durationScale,
        hasToggles = false,
        pageSize = 10,
        showPagination = false,
        series = [],
        onPointClick,
    } = props

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)

    // Hover state for sub-segment highlighting
    const [hoveredSeries, setHoveredSeries] = useState<string | null>(null)

    // Calculate pagination
    const totalItems = data.length
    const totalPages = Math.ceil(totalItems / pageSize)
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = Math.min(startIndex + pageSize, totalItems)
    
    // Get paginated data
    const paginatedData = useMemo(() => {
        if (!showPagination || pageSize <= 0) {
            return data
        }
        return data.slice(startIndex, endIndex)
    }, [data, currentPage, pageSize, showPagination, startIndex, endIndex])

    // Pagination navigation functions
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const goToPage = (pageNumber: number) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber)
        }
    }

    // Reset to first page when data changes
    useMemo(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1)
        }
    }, [totalPages, currentPage])

    if (data.length === 0) {
        return <div className={`analytics-bar-chart horizontal${onPointClick ? ' drillable' : ''}`}>
            <h3>{title}</h3>
            <span>No Data to Display</span>
        </div>
    }

    // Detect if dataKey is a rate field for percentage formatting
    const isRateField = dataKey && (dataKey.toLowerCase().includes('rate') || dataKey.includes('Rate'))

    // Custom X-axis formatter for duration values (horizontal chart uses X-axis for values)
    const xAxisFormatter = (value: number) => {
        if (isDuration && durationScale) {
            // For X-axis, show the scaled numeric value with unit indicator
            return `${value}${durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, dataKey)
    }

    // Function to detect if series have vastly different scales
    const analyzeSeriesScales = useMemo(() => {
        if (series.length < 2) return { needsDualAxis: false, leftSeries: series, rightSeries: [] }

        // Calculate max values for each series
        const seriesStats = series.map(s => {
            const values = paginatedData.map(item => Number(item[s.dataKey]) || 0).filter(v => v > 0)
            const maxValue = Math.max(...values)
            const avgValue = values.reduce((sum, v) => sum + v, 0) / values.length
            
            return {
                ...s,
                maxValue,
                avgValue,
                isPercentage: s.dataKey.toLowerCase().includes('rate') || s.name.toLowerCase().includes('rate') || s.name.includes('%'),
                isDuration: s.isDuration || isDurationField(s.dataKey)
            }
        })

        // Group series by scale characteristics
        const percentageSeries = seriesStats.filter(s => s.isPercentage || s.maxValue <= 1)
        const durationSeries = seriesStats.filter(s => s.isDuration && !s.isPercentage && s.maxValue > 1)
        const countSeries = seriesStats.filter(s => !s.isPercentage && !s.isDuration && s.maxValue > 1)

        // If we have percentage/rate data mixed with count data, use dual axis
        const hasPercentages = percentageSeries.length > 0
        const hasCounts = countSeries.length > 0 || durationSeries.length > 0

        if (hasPercentages && hasCounts) {
            // Put percentages on right axis, everything else on left
            return {
                needsDualAxis: true,
                leftSeries: [...countSeries, ...durationSeries],
                rightSeries: percentageSeries
            }
        }

        // Check for order of magnitude differences between count series
        if (countSeries.length >= 2) {
            const maxOfMaxes = Math.max(...countSeries.map(s => s.maxValue))
            const minOfMaxes = Math.min(...countSeries.map(s => s.maxValue))
            
            // If there's more than 10x difference, split into two axes
            if (maxOfMaxes / minOfMaxes > 10) {
                const sortedBySize = [...countSeries].sort((a, b) => b.maxValue - a.maxValue)
                // Find natural break: where ratio between first series and this one exceeds 5x
                let splitIndex = 1
                for (let i = 1; i < sortedBySize.length; i++) {
                    if (sortedBySize[0].maxValue / sortedBySize[i].maxValue > 5) {
                        splitIndex = i
                        break
                    }
                }
                
                // Identify which keys go on left vs right
                const leftKeys = new Set(sortedBySize.slice(0, splitIndex).map(s => s.dataKey))
                
                // Preserve original series order for rendering
                const leftSeriesOrdered = seriesStats.filter(s => leftKeys.has(s.dataKey))
                const rightSeriesOrdered = seriesStats.filter(s => !leftKeys.has(s.dataKey) && !s.isDuration && !s.isPercentage)
                
                return {
                    needsDualAxis: true,
                    leftSeries: [...leftSeriesOrdered, ...durationSeries],
                    rightSeries: rightSeriesOrdered
                }
            }
        }

        return { needsDualAxis: false, leftSeries: seriesStats, rightSeries: [] }
    }, [series, paginatedData])

    // Custom formatters for different axis types (for horizontal chart, these apply to X-axes)
    const leftAxisFormatter = (value: number) => {
        const leftSeriesItem = analyzeSeriesScales.leftSeries[0]
        if (leftSeriesItem?.isDuration && leftSeriesItem?.durationScale) {
            return `${value}${leftSeriesItem.durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, leftSeriesItem?.dataKey)
    }

    const rightAxisFormatter = (value: number) => {
        const rightSeriesItem = analyzeSeriesScales.rightSeries[0]
        if (rightSeriesItem?.isPercentage) {
            return `${value}%`
        }
        if (rightSeriesItem?.isDuration && rightSeriesItem?.durationScale) {
            return `${value}${rightSeriesItem.durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, rightSeriesItem?.dataKey)
    }

    // Use deterministic color based on chart title/dataKey combination
    const barColor = color || getAnalyticsColor(`${title || ''}-${dataKey}`)

    // Calculate optimal Y-axis width for category labels - use paginatedData
    const yAxisWidth = calculateYAxisWidth(paginatedData, labelKey, 80)

    // Calculate top margin based on toggle presence
    const topMargin = useMemo(() => {
        const baseMargin = hasToggles ? 60 : 20
        const modifier = props.marginModifier || 1.0
        return Math.round(baseMargin * modifier)
    }, [hasToggles, props.marginModifier])

    // Calculate left margin based on modifier
    const leftMargin = useMemo(() => {
        const baseMargin = 20
        const modifier = props.marginModifier || 1.0
        return Math.round(baseMargin * modifier)
    }, [props.marginModifier])

    // Create a custom label component that includes dataKey context
    const CustomLabelWithDataKey = (labelProps: any) => {
        let formattedValue = labelProps.value
        
        if (isDuration && labelProps.payload && labelProps.payload[`${dataKey}_formatted`]) {
            // For duration fields, use the pre-formatted duration string
            formattedValue = labelProps.payload[`${dataKey}_formatted`]
        } else if (isDuration && typeof labelProps.value === 'number' && durationScale) {
            // Fallback: if we don't have the formatted value, format the original value
            // Convert scaled value back to original milliseconds for formatting
            const originalValue = labelProps.value * durationScale.scale
            formattedValue = formatDuration(originalValue)
        } else if (isRateField) {
            formattedValue = `${labelProps.value}%`
        } else if (typeof labelProps.value === 'number' && dataKey && dataKey.toLowerCase().includes('cost')) {
            // Format currency values for cost fields
            if (labelProps.value < 1) {
                formattedValue = `$${labelProps.value.toFixed(3).replace(/\.?0+$/, '')}`
            } else if (labelProps.value >= 100) {
                formattedValue = `$${Math.round(labelProps.value).toLocaleString()}`
            } else {
                formattedValue = `$${labelProps.value.toFixed(2)}`
            }
        }
        
        return <CustomLabel {...labelProps} value={formattedValue} fill={barColor} isDuration={isDuration} />
    }

    // Pagination controls component
    const PaginationControls = () => (
        <ChartPaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            showPagination={showPagination}
            onPrevPage={goToPrevPage}
            onNextPage={goToNextPage}
            onGoToPage={goToPage}
        />
    )

    const emitPointClick = (entry: any) => {
        if (!onPointClick || !labelKey) {
            return
        }
        const row = entry?.payload || entry
        const labelValue = row?.[labelKey]
        if (labelValue === undefined || labelValue === null) {
            return
        }
        onPointClick({
            chartType: 'top',
            labelKey,
            labelValue,
            row,
        })
    }

    return <div className={`analytics-bar-chart horizontal${onPointClick ? ' drillable' : ''}`} style={{ position: 'relative' }}>
        <div className="analytics-chart-view">
            <ResponsiveContainer width="100%" height="100%">
                {analyzeSeriesScales.needsDualAxis ? (
                    <ComposedChart
                        data={paginatedData}
                        layout="vertical"
                        barGap={0}
                        margin={{
                            top: topMargin,
                            right: leftMargin,
                            bottom: 0,
                            left: leftMargin,
                        }}
                    >
                        <CartesianGrid strokeDashArray="3 3" stroke="#e0e6ed" />
                        {/* Bottom X-axis for primary metrics (counts, durations) */}
                        <XAxis
                            xAxisId="left"
                            type="number"
                            orientation="bottom"
                            tickFormatter={leftAxisFormatter}
                        />
                        {/* Top X-axis for secondary metrics (percentages) */}
                        <XAxis
                            xAxisId="right"
                            type="number"
                            orientation="top"
                            tickFormatter={rightAxisFormatter}
                        />
                        <YAxis
                            type="category"
                            dataKey={labelKey}
                            interval={0}
                            width={yAxisWidth - 10}
                            tickLine={false}
                            tickFormatter={(value) => formatYAxisLabelWithWrap(value, 25)}
                        />
                        {/* Render left axis series */}
                        {analyzeSeriesScales.leftSeries.map((s, index) => (
                            <Bar
                                key={`bar-left-${s.dataKey}`}
                                xAxisId="left"
                                dataKey={s.dataKey}
                                fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)}
                                name={s.name}
                                onClick={emitPointClick}
                                onMouseEnter={() => setHoveredSeries(s.dataKey)}
                                onMouseLeave={() => setHoveredSeries(null)}
                                opacity={hoveredSeries !== null && hoveredSeries !== s.dataKey ? 0.5 : 1}
                            >
                                <LabelList
                                    dataKey={s.dataKey}
                                    content={(labelProps: any) => {
                                        let formattedValue = labelProps.value
                                        if (s.isDuration && labelProps.payload && labelProps.payload[`${s.dataKey}_formatted`]) {
                                            formattedValue = labelProps.payload[`${s.dataKey}_formatted`]
                                        } else if (s.isDuration && typeof labelProps.value === 'number' && s.durationScale) {
                                            const originalValue = labelProps.value * s.durationScale.scale
                                            formattedValue = formatDuration(originalValue)
                                        }
                                        return <CustomLabel {...labelProps} value={formattedValue} fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)} isDuration={s.isDuration} />
                                    }}
                                />
                            </Bar>
                        ))}
                        {/* Render right axis series */}
                        {analyzeSeriesScales.rightSeries.map((s, index) => (
                            <Bar
                                key={`bar-right-${s.dataKey}`}
                                xAxisId="right"
                                dataKey={s.dataKey}
                                fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index + analyzeSeriesScales.leftSeries.length)}
                                name={s.name}
                                onClick={emitPointClick}
                                onMouseEnter={() => setHoveredSeries(s.dataKey)}
                                onMouseLeave={() => setHoveredSeries(null)}
                                opacity={hoveredSeries !== null && hoveredSeries !== s.dataKey ? 0.5 : 1}
                            >
                                <LabelList
                                    dataKey={s.dataKey}
                                    content={(labelProps: any) => {
                                        let formattedValue = labelProps.value
                                        if (s.isPercentage) {
                                            formattedValue = `${labelProps.value}%`
                                        } else if (s.isDuration && labelProps.payload && labelProps.payload[`${s.dataKey}_formatted`]) {
                                            formattedValue = labelProps.payload[`${s.dataKey}_formatted`]
                                        }
                                        return <CustomLabel {...labelProps} value={formattedValue} fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index + analyzeSeriesScales.leftSeries.length)} isDuration={s.isDuration} />
                                    }}
                                />
                            </Bar>
                        ))}
                        <Tooltip content={<CustomTooltip isDuration={isDuration} hoveredSeries={hoveredSeries} />} />
                        <Legend
                            wrapperStyle={{ marginTop: 6, paddingTop: 12, left: 0, right: 0 }}
                            align="center"
                            verticalAlign="top"
                            iconType="circle"
                            formatter={(value, entry, index) => {
                                const series = analyzeSeriesScales.leftSeries.concat(analyzeSeriesScales.rightSeries)
                                const found = series.find(s => s.name === value)
                                const color = found ? found.color || getAnalyticsColor(`${title || ''}-${found.dataKey}`, index) : '#000'
                                return <span style={{ color }}>{value}</span>
                            }}
                        />
                    </ComposedChart>
                ) : (
                    <BarChart
                        data={paginatedData}
                        layout="vertical"
                        barGap={0}
                        margin={{
                            top: topMargin,
                            right: leftMargin,
                            bottom: 0,
                            left: leftMargin,
                        }}
                    >
                        <CartesianGrid strokeDashArray="3 3" stroke="#e0e6ed" />
                        <XAxis
                            type="number"
                            tickFormatter={xAxisFormatter}
                        />
                        <YAxis
                            type="category"
                            dataKey={labelKey}
                            interval={0}
                            width={yAxisWidth - 10}
                            tickLine={false}
                            tickFormatter={(value) => formatYAxisLabelWithWrap(value, 25)}
                        />
                        {series.length > 0 ? series.map((s, index) => {
                            const seriesColor = s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`)
                            return <Bar
                                key={index}
                                dataKey={s.dataKey}
                                fill={seriesColor}
                                onClick={emitPointClick}
                                onMouseEnter={() => setHoveredSeries(s.dataKey)}
                                onMouseLeave={() => setHoveredSeries(null)}
                                opacity={hoveredSeries !== null && hoveredSeries !== s.dataKey ? 0.5 : 1}
                            >
                                <LabelList
                                    dataKey={s.dataKey}
                                    content={<CustomLabelWithDataKey />}
                                />
                            </Bar>
                        }) : <Bar dataKey={dataKey} fill={barColor} onClick={emitPointClick}>
                            <LabelList
                                dataKey={dataKey}
                                content={<CustomLabelWithDataKey />}
                            />
                        </Bar>}
                        <Tooltip content={<CustomTooltip isDuration={isDuration} hoveredSeries={hoveredSeries} />} />
                    </BarChart>
                )}
            </ResponsiveContainer>
        </div>
        <PaginationControls />
    </div>
}
